"use client";

import React, { createContext, useContext, useState, useCallback } from "react";
import {
  IconCircleCheck,
  IconAlertCircle,
  IconInfoCircle,
  IconAlertTriangle,
  IconX,
} from "@tabler/icons-react";
import { useBrowserStore } from "@/utils/browserStore";
import { themeStore, localeStore } from "@/utils/themeStore";
import { getTranslations } from "@/i18n/translations";
import type { ToastType, WhiteboardToastNotice } from "@/shared/types";

export type { ToastType, WhiteboardToastNotice };

export interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  /** Optional when the toast notification consists solely of a headline title without secondary detail text. */
  description?: string;
  /** Optional when using the default 3500ms auto-dismiss timeout. */
  duration?: number;
}

export interface ToastProviderProps {
  readonly children: React.ReactNode;
  /** Optional when rendering standard DOM toasts; set true in embedded iframe shells to suppress DOM toast viewport rendering. */
  readonly headless?: boolean;
  /** Optional callback invoked whenever a toast notification is emitted (used by embedded iframe shells to forward notices over postMessage). */
  readonly onToast?: (notice: WhiteboardToastNotice) => void;
}

interface ToastContextValue {
  toast: (options: Omit<ToastItem, "id">) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({
  children,
  headless = false,
  onToast,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [theme] = useBrowserStore(themeStore);
  const [locale] = useBrowserStore(localeStore);
  const isDark = theme === "dark";
  const tWorkspace = getTranslations(locale).workspace;

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    ({ type, title, description, duration = 3500 }: Omit<ToastItem, "id">) => {
      onToast?.({
        type,
        title,
        ...(description !== undefined ? { description } : {}),
      });

      if (headless) {
        return;
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const newToast: ToastItem = { id, type, title, description, duration };

      setToasts((prev) => [...prev, newToast]);

      if (duration > 0) {
        setTimeout(() => {
          dismiss(id);
        }, duration);
      }
    },
    [dismiss, headless, onToast]
  );

  const success = useCallback(
    (title: string, description?: string) => toast({ type: "success", title, description }),
    [toast]
  );
  const error = useCallback(
    (title: string, description?: string) => toast({ type: "error", title, description }),
    [toast]
  );
  const info = useCallback(
    (title: string, description?: string) => toast({ type: "info", title, description }),
    [toast]
  );
  const warning = useCallback(
    (title: string, description?: string) => toast({ type: "warning", title, description }),
    [toast]
  );

  return (
    <ToastContext.Provider value={{ toast, success, error, info, warning, dismiss }}>
      {children}
      {/* Toast Viewport */}
      {!headless && (
        <div
          aria-live="polite"
          className={`pointer-events-none fixed bottom-16 sm:bottom-4 right-3 sm:right-4 z-50 flex max-w-[calc(100%-1.5rem)] sm:max-w-md flex-col gap-2 ${
            isDark ? "dark" : ""
          }`}
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              role={t.type === "error" ? "alert" : "status"}
              data-type={t.type}
              className="pointer-events-auto flex w-80 max-w-full items-start gap-3 rounded-lg border p-3.5 shadow-xl transition-all duration-200 backdrop-blur border-zinc-200 bg-white/95 text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900/95 dark:text-zinc-200 data-[type=success]:border-emerald-200 data-[type=success]:text-emerald-800 dark:data-[type=success]:border-emerald-500/30 dark:data-[type=success]:text-emerald-300 data-[type=error]:border-rose-200 data-[type=error]:text-rose-800 dark:data-[type=error]:border-rose-500/30 dark:data-[type=error]:text-rose-300 data-[type=warning]:border-amber-200 data-[type=warning]:text-amber-800 dark:data-[type=warning]:border-amber-500/30 dark:data-[type=warning]:text-amber-300"
            >
              <div className="mt-0.5 shrink-0">
                {t.type === "success" && (
                  <IconCircleCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                )}
                {t.type === "error" && (
                  <IconAlertCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                )}
                {t.type === "warning" && (
                  <IconAlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                )}
                {t.type === "info" && (
                  <IconInfoCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold break-words text-zinc-900 dark:text-zinc-100">
                  {t.title}
                </p>
                {t.description && (
                  <p className="mt-0.5 text-xs leading-relaxed break-words text-zinc-600 dark:text-zinc-400">
                    {t.description}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="shrink-0 rounded p-0.5 transition-colors cursor-pointer text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-zinc-200"
                aria-label={tWorkspace.dismissToastAria}
              >
                <IconX className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
